import { createSlice } from '@reduxjs/toolkit';

import { CONNECTION_STATE_DATA } from '../Actions/Constants';
import { CONNECTION_STATE_CONNECTED } from '../Helpers/Chat/Constant';
import { clearState } from './clearSlice';

export const LIVE_LOCATION_STATUS = { ACTIVE: 'active', ENDED: 'ended' };

const LIVE_LOCATION_EVENT = { POSITION: 'position', ENDED: 'ended' };

const LIVE_LOCATION_END_REASON = { EXPIRED: 'expired' };

// The live state is kept apart from the message: its meta_data never changes after sending.
const initialState = {
    byMessageId: {},
    staleIds: {},
    // Bumped on every reconnect so an open pop-up reloads the shares of its chat.
    syncRound: 0,
    // Kept outside the virtualised thread, which may unmount the card behind the pop-up.
    openPopup: null,
};

const toNumberOrNull = value => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
};

const toStored = item => ({
    messageId: item.messageId,
    chatJid: item.chatJid || '',
    chatType: item.chatType || '',
    sharer: item.sharer || '',
    sharerJid: item.sharerJid || '',
    duration: item.duration || '',
    startedAt: toNumberOrNull(item.startedAt),
    endsAt: toNumberOrNull(item.endsAt),
    status: item.status === LIVE_LOCATION_STATUS.ENDED ? LIVE_LOCATION_STATUS.ENDED : LIVE_LOCATION_STATUS.ACTIVE,
    endedAt: toNumberOrNull(item.endedAt),
    endReason: item.endReason || '',
    position: item.position || null,
    positionAt: toNumberOrNull(item.positionAt),
    version: Number(item.version) || 0,
});

// An event can reach a share not loaded yet, e.g. one started after opening.
const fromEvent = event =>
    toStored({
        messageId: event.messageId,
        chatJid: event.chatJid,
        chatType: event.chatType,
        sharer: event.sharer,
        sharerJid: event.sharerJid,
        status: LIVE_LOCATION_STATUS.ACTIVE,
    });

// An expired share ended at its deadline; other ends are dated on receipt.
const endedAtOf = (event, held) =>
    (event.reason === LIVE_LOCATION_END_REASON.EXPIRED && held.endsAt) ||
    toNumberOrNull(event.receivedAt) ||
    held.endsAt;

const liveLocationSlice = createSlice({
    name: 'liveLocation',
    initialState,
    reducers: {
        // An answer older than the copy already held is dropped, so events stay in order.
        upsertLiveLocations(state, action) {
            (action.payload || []).forEach(item => {
                if (!item?.messageId) return;
                const held = state.byMessageId[item.messageId];
                if (held && held.version > (Number(item.version) || 0)) return;
                state.byMessageId[item.messageId] = toStored(item);
                delete state.staleIds[item.messageId];
            });
        },
        applyLiveLocationEvent(state, action) {
            const event = action.payload || {};
            if (!event.messageId) return;
            const held = state.byMessageId[event.messageId];
            const version = Number(event.version) || 0;
            if (held && held.version >= version) return;

            const next = { ...(held || fromEvent(event)), version };
            next.endsAt = toNumberOrNull(event.endsAt) || next.endsAt;
            if (event.event === LIVE_LOCATION_EVENT.POSITION && event.position) {
                next.position = event.position;
                next.positionAt = toNumberOrNull(event.positionAt) || next.positionAt;
            }
            // The last position stays: an ended share holds the pin where it stopped.
            if (event.event === LIVE_LOCATION_EVENT.ENDED) {
                next.status = LIVE_LOCATION_STATUS.ENDED;
                next.endedAt = endedAtOf(event, next);
                next.endReason = event.reason || '';
            }
            state.byMessageId[event.messageId] = next;
        },
        markLiveLocationsStale(state, action) {
            (action.payload || []).forEach(messageId => {
                state.staleIds[messageId] = true;
            });
        },
        openLocationPopup(state, action) {
            const { chatId, msgId } = action.payload || {};
            state.openPopup = chatId && msgId ? { chatId, msgId } : null;
        },
        closeLocationPopup(state) {
            state.openPopup = null;
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
        // Events sent while offline are not replayed, so every share is read again.
        builder.addMatcher(
            action => action.type === CONNECTION_STATE_DATA && action.payload?.data === CONNECTION_STATE_CONNECTED,
            state => {
                Object.keys(state.byMessageId).forEach(messageId => {
                    state.staleIds[messageId] = true;
                });
                state.syncRound += 1;
            },
        );
    },
});

export const {
    upsertLiveLocations,
    applyLiveLocationEvent,
    markLiveLocationsStale,
    openLocationPopup,
    closeLocationPopup,
} = liveLocationSlice.actions;

export const selectLiveLocation = (state, messageId) => state.liveLocation.byMessageId[messageId];

export const selectIsLiveLocationStale = (state, messageId) => Boolean(state.liveLocation.staleIds[messageId]);

export const selectLiveLocations = state => state.liveLocation.byMessageId;

export const selectLiveLocationSyncRound = state => state.liveLocation.syncRound;

export const selectOpenLocationPopup = state => state.liveLocation.openPopup;

export default liveLocationSlice.reducer;
